/*
  <offer-wallet>: floating tab + slide-in <dialog> of offers (snippets/offer-wallet.liquid).

  The dialog is opened with showModal(), so the browser provides the backdrop,
  Escape handling and focus trapping. Closing always goes through close(), which
  plays the slide-out animation and then lets the dialog's own 'close' event
  release the page scroll lock. Nothing here depends on optional markup, so a
  missing product strip or code button can never leave the page stuck.
*/
class OfferWallet extends HTMLElement {
  constructor() {
    super();
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onClosed = this.onClosed.bind(this);
    this.closing = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-offer-wallet-open]');
    this.toast = this.querySelector('[data-offer-wallet-toast]');
    if (!this.dialog || !this.openButton) return;

    if (typeof this.dialog.showModal !== 'function') {
      // Browsers without <dialog> support (pre-2022): hide the tab rather than show a broken panel.
      this.openButton.hidden = true;
      return;
    }

    // Delegated so the header icon (outside this element) opens it too.
    document.addEventListener('click', this.onDocumentClick);
    this.querySelectorAll('[data-offer-wallet-close]').forEach((button) => button.addEventListener('click', this.close));
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onClosed);
    this.querySelectorAll('[data-offer-wallet-copy]').forEach((button) => {
      button.addEventListener('click', () => this.copyCode(button));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    if (this.dialog && this.dialog.open) this.dialog.close();
    document.body.classList.remove('overflow-hidden');
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-offer-wallet-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  open() {
    if (this.dialog.open) return;
    this.closing = false;
    this.dialog.classList.remove('is-closing');
    this.dialog.showModal();
    document.body.classList.add('overflow-hidden');
    const panel = this.dialog.querySelector('.offer-wallet__panel');
    if (panel) panel.scrollTop = 0;
  }

  close() {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;

    const finish = () => {
      if (!this.closing) return;
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      if (this.dialog.open) this.dialog.close();
    };

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      finish();
      return;
    }
    this.dialog.classList.add('is-closing');
    this.dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 400);
  }

  // A click on the backdrop lands on the <dialog> itself, not on the panel inside it.
  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  // Escape: run our animated close instead of the instant native one.
  onCancel(event) {
    event.preventDefault();
    this.close();
  }

  onClosed() {
    this.closing = false;
    this.dialog.classList.remove('is-closing');
    document.body.classList.remove('overflow-hidden');
  }

  async copyCode(button) {
    const code = button.dataset.offerWalletCopy;
    if (!code) return;
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }
    if (!copied) copied = this.copyFallback(code);

    const label = button.querySelector('[data-offer-wallet-copy-label]');
    if (label) {
      const original = label.dataset.original || label.textContent;
      label.dataset.original = original;
      label.textContent = copied ? 'Copied' : 'Copy failed';
      window.setTimeout(() => {
        label.textContent = original;
      }, 2000);
    }
    this.showToast(copied ? `Code ${code} copied` : `Could not copy. Your code is ${code}`);
  }

  copyFallback(text) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    this.dialog.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    area.remove();
    return ok;
  }

  showToast(text) {
    if (!this.toast) return;
    this.toast.textContent = text;
    this.toast.classList.add('is-visible');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toast.classList.remove('is-visible');
    }, 2200);
  }
}

if (!customElements.get('offer-wallet')) {
  customElements.define('offer-wallet', OfferWallet);
}
